/**
 * BrandingContext — app-wide company branding state
 * Fetches GET /api/v1/public/branding (no auth required)
 * Exposes useBranding() hook and refreshBranding() function
 *
 * Also sets window.__companyTimezone for date formatting utils.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { authedFetch } from '@/lib/api';
import { setCompanyTimezone } from '../utils/dateUtils'

export interface CompanySettings {
  id: number
  company_name: string
  logo_url: string
  logo_dark_url: string
  primary_color: string
  accent_color: string
  portal_tagline: string
  portal_welcome_msg: string
  address: string
  phone: string
  website: string
  email: string
  timezone: string
  created_at: string
  updated_at: string
}

const DEFAULT_SETTINGS: CompanySettings = {
  id: 0,
  company_name: 'Tenon',
  logo_url: '',
  logo_dark_url: '',
  primary_color: 'var(--brand-gold)',
  accent_color: 'var(--brand-gold)',
  portal_tagline: '',
  portal_welcome_msg: '',
  address: '',
  phone: '',
  website: '',
  email: '',
  timezone: 'America/Montreal',
  created_at: '',
  updated_at: '',
}

interface BrandingContextValue {
  branding: CompanySettings
  loading: boolean
  refresh: () => Promise<void>
}

const BrandingContext = createContext<BrandingContextValue>({
  branding: DEFAULT_SETTINGS,
  loading: false,
  refresh: async () => {},
})

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const [branding, setBranding] = useState<CompanySettings>(DEFAULT_SETTINGS)
  const [loading, setLoading] = useState(true)

  const fetchBranding = useCallback(async () => {
    try {
      const res = await authedFetch('/api/v1/public/branding', {}, { redirectOnAuthFailure: false })
      if (res.ok) {
        const data: CompanySettings = await res.json()
        setBranding(data)
        setCompanyTimezone(data.timezone || 'America/Montreal')
        document.documentElement.style.setProperty('--brand-primary', data.primary_color || '#EFF890')
        document.documentElement.style.setProperty('--brand-accent', data.accent_color || '#EFF890')
      }
    } catch {
      // silently fail — use defaults
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchBranding()
  }, [fetchBranding])

  return (
    <BrandingContext.Provider value={{ branding, loading, refresh: fetchBranding }}>
      {children}
    </BrandingContext.Provider>
  )
}

export function useBranding() {
  return useContext(BrandingContext)
}
